/**
 * CB16 GST engine (India only).
 *
 * Pure, framework-free calculation shared by cart, checkout, payment intent,
 * order summary and invoice so every surface shows the same numbers.
 *
 * Rules:
 *  - Place of supply = customer's delivery/billing state.
 *  - Same state as the business (Maharashtra) → CGST + SGST (half rate each).
 *  - Another Indian state → IGST (full rate).
 *  - Outside India → not supported in this phase (no tax applied).
 *
 * The rate, GSTIN, business state, effective-from date, HSN and the
 * inclusive/exclusive mode are all configuration — nothing is hard-coded here.
 */

export type GstMode = "cgst_sgst" | "igst" | "none";

export type GstConfig = {
  enabled: boolean;
  /** Legacy combined rate — used only as a fallback for the split rates. */
  ratePct: number;
  /** Intra-state CGST rate (%). */
  cgstRatePct: number;
  /** Intra-state SGST rate (%). */
  sgstRatePct: number;
  /** Inter-state IGST rate (%). */
  igstRatePct: number;
  inclusive: boolean;
  label: string;
  gstin?: string;
  businessState?: string;
  effectiveFrom?: string;
  defaultHsn?: string;
};

export type GstBreakdown = {
  /** Which tax split applies. */
  mode: GstMode;
  label: string;
  /** Effective total rate applied for this order (CGST+SGST, or IGST). */
  ratePct: number;
  /** Component rates applied to this order. */
  cgstRatePct: number;
  sgstRatePct: number;
  igstRatePct: number;
  inclusive: boolean;
  /** Net value the tax is calculated on. */
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  totalGst: number;
  /** Resolved customer state (place of supply), "" when unknown. */
  placeOfSupply: string;
  /** Seller GSTIN, when configured. */
  gstin?: string;
  /** Customer GSTIN for B2B orders, when supplied. */
  customerGstin?: string;
  /** HSN / tax classification, when configured. */
  hsn?: string;
};

export const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
] as const;

/** Basic GSTIN shape check (15 chars: 2 digits, 5 letters, 4 digits, letter, digit/letter, Z, checksum). */
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export function isValidGstin(value: string): boolean {
  return GSTIN_REGEX.test(value.trim().toUpperCase());
}

function norm(value: string | undefined | null): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function isIndia(country: string | undefined | null): boolean {
  const c = norm(country);
  return c === "" || c === "india" || c === "in" || c === "bharat";
}

export function sameState(a: string | undefined | null, b: string | undefined | null): boolean {
  return norm(a) !== "" && norm(a) === norm(b);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Clamp a configured rate to a safe, non-negative number. */
function safeRate(value: number | undefined | null): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Resolved component rates, falling back to the legacy combined rate. */
function rates(config: GstConfig) {
  const legacy = safeRate(config.ratePct);
  const cgst = safeRate(config.cgstRatePct) || round2(legacy / 2);
  const sgst = safeRate(config.sgstRatePct) || round2(legacy - round2(legacy / 2));
  const igst = safeRate(config.igstRatePct) || legacy;
  return { cgst, sgst, igst };
}

function configActive(config: GstConfig, at: Date): boolean {
  if (!config.enabled) return false;
  const r = rates(config);
  if (!(r.cgst + r.sgst + r.igst > 0)) return false;
  if (config.effectiveFrom) {
    const from = new Date(config.effectiveFrom);
    if (!Number.isNaN(from.getTime()) && at < from) return false;
  }
  return true;
}

const EMPTY: Omit<
  GstBreakdown,
  "label" | "ratePct" | "cgstRatePct" | "sgstRatePct" | "igstRatePct" | "inclusive" | "placeOfSupply"
> = {
  mode: "none",
  taxable: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
  totalGst: 0,
};

/**
 * Compute the GST split for a taxable amount (goods after discount, plus
 * shipping — a composite supply). `gross` is the amount as currently priced;
 * in inclusive mode the tax is extracted from it, in exclusive mode the tax
 * is added on top.
 */
export function computeGst(input: {
  gross: number;
  customerState?: string | null;
  customerCountry?: string | null;
  customerGstin?: string | null;
  config: GstConfig;
  at?: Date;
}): GstBreakdown {
  const { config } = input;
  const at = input.at ?? new Date();
  const r = rates(config);
  const placeOfSupply = (input.customerState ?? "").trim();
  const base: GstBreakdown = {
    ...EMPTY,
    label: config.label || "GST",
    ratePct: round2(r.cgst + r.sgst),
    cgstRatePct: r.cgst,
    sgstRatePct: r.sgst,
    igstRatePct: r.igst,
    inclusive: config.inclusive,
    placeOfSupply,
    ...(config.gstin ? { gstin: config.gstin } : {}),
    ...(input.customerGstin ? { customerGstin: input.customerGstin.trim().toUpperCase() } : {}),
    ...(config.defaultHsn ? { hsn: config.defaultHsn } : {}),
    taxable: round2(input.gross),
  };

  if (!configActive(config, at)) return { ...base, mode: "none" };
  // International selling is out of scope for this phase.
  if (!isIndia(input.customerCountry)) return { ...base, mode: "none" };
  // Without a place of supply we cannot decide CGST+SGST vs IGST.
  if (placeOfSupply === "") return { ...base, mode: "none" };

  const intra = sameState(placeOfSupply, config.businessState);
  // Effective rate for this order comes straight from the configured components.
  const appliedPct = intra ? round2(r.cgst + r.sgst) : r.igst;
  const rate = appliedPct / 100;
  const taxable = config.inclusive ? round2(input.gross / (1 + rate)) : round2(input.gross);
  const totalGst = config.inclusive ? round2(input.gross - taxable) : round2(taxable * rate);

  if (intra) {
    // Split by the configured CGST : SGST ratio (9/9 → equal halves).
    const cgst = appliedPct > 0 ? round2((totalGst * r.cgst) / appliedPct) : 0;
    return {
      ...base,
      mode: "cgst_sgst",
      ratePct: appliedPct,
      taxable,
      cgst,
      sgst: round2(totalGst - cgst),
      igst: 0,
      totalGst,
    };
  }
  return {
    ...base,
    mode: "igst",
    ratePct: appliedPct,
    taxable,
    cgst: 0,
    sgst: 0,
    igst: totalGst,
    totalGst,
  };
}

/** Amount added to the payable total (0 when prices already include tax). */
export function gstPayableAddition(gst: GstBreakdown | null | undefined): number {
  if (!gst || gst.mode === "none" || gst.inclusive) return 0;
  return gst.totalGst;
}
